import React, { memo } from "react";
import {
  Grid,
  Box,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { Data } from "../../../store/cert/types";
import { getPEMFormat } from "../../../store/cert/queries";
import { FormikProps, useFormikContext, FastField } from "formik";
import ArrayInput from "../../../common/arrayInput/ArrayInput";
import CertDialog from "./CertDialog";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import TextInput from "../../../common/formElements/TextInput";
import CustomizedCheckbox from "../../../common/formElements/Checkbox";

interface CertDetailSection {
  isCertNew?: boolean;
}

const CertDetailSection = ({ isCertNew }: CertDetailSection) => {
  const formProps = useFormikContext<FormikProps<Data>>();
  const { setFieldValue } = formProps;
  const values = (formProps.values as unknown) as Data;
  const errors = (formProps.errors as unknown) as Data;

  return (
    <Grid item xs={12}>
      <SectionWrapper title="General Info" isChild>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.Country" label="Country" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.State" label="State" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.Locality" label="Locality" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.Org" label="Org" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.OrgUnit" label="OrgUnit" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.Title" label="Title" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput fieldname="attributes.Email" label="Email" />
          </Grid>
        </Grid>
      </SectionWrapper>

      <SectionWrapper isChild title="Subject Alternative Name">
        <>
          <FormControlLabel
            control={
              <CustomizedCheckbox checked={values.attributes?.SANCritical} />
            }
            label="SAN Critical"
          />
          <FastField name="attributes.SAN">
            {() => (
              <ArrayInput
                label=""
                value={values.attributes.SAN || []}
                onChange={(data: string[]) => {
                  setFieldValue("attributes.SAN", data);
                }}
              />
            )}
          </FastField>
          {errors?.attributes?.SAN && (
            <FormHelperText error>{errors?.attributes?.SAN}</FormHelperText>
          )}
        </>
      </SectionWrapper>

      <SectionWrapper isChild title="Revocation OCSP URL">
        <>
          <FormControlLabel
            control={
              <CustomizedCheckbox checked={values.attributes?.OSCPCritical} />
            }
            label="OSCP Critical"
          />
          <FastField name="attributes.OCSP">
            {() => (
              <ArrayInput
                label=""
                value={values.attributes.OCSP || []}
                onChange={(data: string[]) => {
                  setFieldValue("attributes.OCSP", data);
                }}
              />
            )}
          </FastField>
          {errors?.attributes?.OCSP && (
            <FormHelperText error>{errors?.attributes?.OCSP}</FormHelperText>
          )}
        </>
      </SectionWrapper>

      <SectionWrapper isChild title="Validity">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={4}>
            <TextInput
              fieldname="attributes.ValidityInYears"
              label="Validity in years"
              type="number"
            />
          </Grid>
          {!isCertNew && (
            <Grid item xs={12} md={4}>
              <InputLabel shrink>Start Date</InputLabel>
              <Box>
                <Typography>
                  {values.attributes?.DateStart
                    ? dayjs(values.attributes?.DateStart).format(
                        "DD MMM YYYY hh:mm:ss a"
                      )
                    : "N/A"}
                </Typography>
              </Box>
            </Grid>
          )}
          {!isCertNew && (
            <Grid item xs={12} md={4}>
              <InputLabel shrink>End Date</InputLabel>
              <Box>
                <Typography>
                  {values.attributes?.DateEnd
                    ? dayjs(values.attributes?.DateEnd).format(
                        "DD MMM YYYY hh:mm:ss a"
                      )
                    : "N/A"}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </SectionWrapper>

      {!isCertNew && (
        <SectionWrapper title="Tools" isChild>
          <Grid container alignItems="baseline" spacing={2}>
            <Grid item xs={4}>
              <CertDialog
                title={"Certificate in PEM Format"}
                certData={getPEMFormat(values.attributes?.X509)}
              />
            </Grid>
            <Grid item xs={4}>
              <CertDialog
                title={"Certificate in DER Format"}
                certData={values.attributes?.X509DER}
              />
            </Grid>
            <Grid item xs={4}>
              <CertDialog
                title={"Certificate Signing Request"}
                certData={getPEMFormat(values.attributes?.CSR, true)}
              />
            </Grid>
          </Grid>
        </SectionWrapper>
      )}
    </Grid>
  );
};

export default memo(CertDetailSection);
